import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Select,
  Divider,
  InputNumber,
  Space,
  Tag,
  DatePicker,
  Radio,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import {
  fetchData,
  fetchLocation,
  fetchSubCounties,
  fetchSubLocation,
  fetchSubsidySource,
} from "../../../features/fetch/fetchSlice";
import { damStatusData, wtrSupplyStatusData } from "../../../data";
import dayjs from "dayjs";
import { normalizeDateToLocalYear } from "../../../utils";

const { TextArea } = Input;
const WaterSupplyAdd = () => {
  const urlPipelineType = "pipeline_type/fetch_pipelinetypes";
  const urlTreatType = "treatment_type/fetch_treatmenttypes";
  const urlWtrSrce = "water_sources/fetch_watersourcess";
  const urlWtrAppl = "water_application_mode/fetch_waterapplicationmodes";
  const urlCrop = "main_crop/fetch_maincrops";
  const urlPathway = "api/v2/irrigationPathway/get";
  const urlPressureSys = "water_pressure_system/fetch_waterpressuresystems";
  const urlAgency = "api/v2/agency/get";

  const urlWard = "ward/fetch_wards";
  const urlConstituency = "constituency/fetch_constituencys";

  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const {
    counties,
    subCounties,
    locations,
    subLocations,
    ward,
    constituency,
    subsidySources,
  } = useSelector((state) => state.fetch);
  const {
    activeObjWtrSupply,
    dataWtrSrc,
    dataCrop,
    dataTreatType,
    dataPipelineType,
    dataPathway,
    dataPresSys,
    dataAgency,
    dataConstituency,
    dataWard,
  } = useSelector((state) => ({
    activeObjWtrSupply: state.obj.activeObj["waterSupply"],
    dataWtrSrc: state.fetch.data[urlWtrSrce] || [],
    dataWtrAppl: state.fetch.data[urlWtrAppl] || [],
    dataTreatType: state.fetch.data[urlTreatType] || [],
    dataPipelineType: state.fetch.data[urlPipelineType] || [],
    dataCrop: state.fetch.data[urlCrop] || [],
    dataPathway: state.fetch.data[urlPathway] || [],
    dataPresSys: state.fetch.data[urlPressureSys] || [],
    dataAgency: state.fetch.data[urlAgency] || [],

    dataWard: state.fetch.data[urlWard] || [],
    dataConstituency: state.fetch.data[urlConstituency] || [],
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  function handleCancel() {
    navigate("/rob/water-supply");
  }

  const plainOptions = ["Yes", "No"];

  const [countyName, setCountyName] = useState(
    activeObjWtrSupply?.countyName ?? ""
  );

  const onFinish = async (data) => {
    data.waterId = activeObjWtrSupply?.waterId
      ? activeObjWtrSupply?.waterId
      : null;
    data.waterCropsId = Array.isArray(data?.waterCropsId)
      ? data?.waterCropsId.join(",")
      : data?.waterCropsId;

    data.waterPathwayId = Array.isArray(data?.waterPathwayId)
      ? data?.waterPathwayId.join(",")
      : data?.waterPathwayId;

    data.waterPressureSystemId = Array.isArray(data?.waterPressureSystemId)
      ? data?.waterPressureSystemId.join(",")
      : data?.waterPressureSystemId;

    data.waterSourceFundsId = Array.isArray(data?.waterSourceFundsId)
      ? data?.waterSourceFundsId.join(",")
      : data?.waterSourceFundsId;
    data.waterStartDate = normalizeDateToLocalYear(data?.waterStartDate);
    data.waterEndDate = normalizeDateToLocalYear(data?.waterEndDate);
    const res = await dispatch(
      save({
        ...data,
        url: "water_supply/save_watersupply",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  const [waterCountyId, setWaterCountyId] = useState(
    activeObjWtrSupply?.waterCountyId ?? ""
  );
  const [waterConstId, setWaterConstId] = useState(
    activeObjWtrSupply?.waterConstId ?? ""
  );
  const [waterSubCountyId, setWaterSubCountyId] = useState(
    activeObjWtrSupply?.waterSubCountyId ?? ""
  );
  const [waterLocationId, setWaterLocationId] = useState(
    activeObjWtrSupply?.waterLocationId ?? ""
  );
  const [waterSubLocationId, setWaterSubLocationId] = useState(
    activeObjWtrSupply?.waterSubLocationId ?? ""
  );
  const [waterWardId, setWaterWardId] = useState(
    activeObjWtrSupply?.waterWardId ?? ""
  );

  const handleWaterCountyChange = async (value) => {
    setWaterCountyId(value);
    setWaterConstId("");
    setWaterSubCountyId("");
    setWaterLocationId("");
    setWaterSubLocationId("");
    setWaterWardId("");

    form.setFieldsValue({
      waterCountyId: value,
      waterConstId: "",
      waterSubCountyId: "",
      waterLocationId: "",
      waterSubLocationId: "",
      waterWardId: "",
    });

    await dispatch(fetchSubCounties({ subCountyId: value }));
    await dispatch(
      fetchData({
        url: urlConstituency,
        constCountyId: value,
      })
    );
  };

  const handleWaterConstituencyChange = async (value) => {
    setWaterConstId(value);
    setWaterWardId("");

    form.setFieldsValue({
      waterConstId: value,
      waterWardId: "",
    });

    await dispatch(
      fetchData({
        url: urlWard,
        wdConstId: value,
      })
    );
  };

  const handleWaterSubCountyChange = async (value) => {
    setWaterSubCountyId(value);
    setWaterLocationId("");
    setWaterSubLocationId("");

    form.setFieldsValue({
      waterSubCountyId: value,
      waterLocationId: "",
      waterSubLocationId: "",
    });

    await dispatch(fetchLocation({ locSubId: value }));
  };

  const handleWaterLocationChange = async (value) => {
    setWaterLocationId(value);
    setWaterSubLocationId("");

    form.setFieldsValue({
      waterLocationId: value,
      waterSubLocationId: "",
    });

    await dispatch(fetchSubLocation({ slocLocId: value }));
  };

  const handleWaterSubLocationChange = (value) => {
    setWaterSubLocationId(value);
    form.setFieldsValue({ waterSubLocationId: value });
  };

  const handleWaterWardChange = (value) => {
    setWaterWardId(value);
    form.setFieldsValue({ waterWardId: value });
  };

  const handleDatesChange = (_, allValues) => {
    const { waterStartDate, waterEndDate } = allValues;
    if (waterStartDate && waterEndDate) {
      const diff = dayjs(waterEndDate)?.diff(dayjs(waterStartDate), "day");
      form.setFieldsValue({
        waterTimeFrame: `${diff} day${diff !== 1 ? "s" : ""}`,
      });
    } else {
      form.setFieldsValue({ waterTimeFrame: "" });
    }
  };

  useEffect(() => {
    dispatch(
      fetchData({
        url: urlTreatType,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlWtrSrce,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlWtrAppl,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlCrop,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlPipelineType,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlPathway,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlPressureSys,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlAgency,
        limit: null,
        start: 0,
      })
    );

    dispatch(fetchSubsidySource());
  }, []);

  const initialFormValues = {
    ...activeObjWtrSupply,
    waterStartDate: activeObjWtrSupply?.waterStartDate
      ? dayjs(new Date(activeObjWtrSupply?.waterStartDate))
      : null,
    waterEndDate: activeObjWtrSupply?.waterEndDate
      ? dayjs(new Date(activeObjWtrSupply?.waterEndDate))
      : null,
  };

  useEffect(() => {
    form.setFieldsValue({
      ...activeObjWtrSupply,
      waterStartDate: activeObjWtrSupply?.waterStartDate
        ? dayjs(new Date(activeObjWtrSupply?.waterStartDate))
        : null,
      waterEndDate: activeObjWtrSupply?.waterEndDate
        ? dayjs(new Date(activeObjWtrSupply?.waterEndDate))
        : null,
    });
    if (activeObjWtrSupply?.waterSourceFundsId) {
      form.setFieldsValue({
        waterSourceFundsId: activeObjWtrSupply?.waterSourceFundsId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
    if (activeObjWtrSupply?.waterCropsId) {
      form.setFieldsValue({
        waterCropsId: activeObjWtrSupply?.waterCropsId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
    if (activeObjWtrSupply?.waterPathwayId) {
      form.setFieldsValue({
        waterPathwayId: activeObjWtrSupply?.waterPathwayId
          ?.split(",")
          ?.map((b) => b?.trim()),
        waterPressureSystemId: activeObjWtrSupply?.waterPressureSystemId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
  }, [activeObjWtrSupply, form]);

  useEffect(() => {
    if (activeObjWtrSupply?.waterId) {
      dispatch(
        fetchSubCounties({
          subCountyId: activeObjWtrSupply?.waterCountyId,
        })
      );
      dispatch(
        fetchData({
          url: urlConstituency,
          constCountyId: activeObjWtrSupply?.waterCountyId,
        })
      );
      dispatch(
        fetchData({
          url: urlWard,
          wdConstId: activeObjWtrSupply?.waterConstId,
        })
      );
      dispatch(
        fetchLocation({
          locSubId: activeObjWtrSupply?.waterSubCountyId,
        })
      );
      dispatch(
        fetchSubLocation({
          slocLocId: activeObjWtrSupply?.waterLocationId,
        })
      );
    }
  }, [activeObjWtrSupply, form]);

  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          { title: "Water Supply", path: "/rob/water-supply" },
          { title: "Water Supply Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="form-add-h mb-[1.25rem]">Water Supply Information</div>
        <Divider />
        <div className="w-full mt-[1.75rem]">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
            onValuesChange={handleDatesChange}
          >
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="waterName"
                label={"Name"}
                rules={[
                  {
                    required: true,
                    message: "Please add name",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>

              <Form.Item
                name="waterCountyId"
                label="County"
                rules={[{ required: true, message: "Please select county" }]}
              >
                <Select
                  allowClear
                  options={counties?.map((c) => ({
                    value: c?.countyId,
                    label: c?.countyName,
                  }))}
                  onChange={handleWaterCountyChange}
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase()?.includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              {waterCountyId && (
                <>
                  <Form.Item
                    name="waterConstId"
                    label="Constituency"
                    rules={[
                      { required: true, message: "Please select constituency" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={dataConstituency?.map((c) => ({
                        value: c?.constId,
                        label: c?.constName,
                      }))}
                      onChange={handleWaterConstituencyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    name="waterSubCountyId"
                    label="Subcounty"
                    rules={[
                      { required: true, message: "Please select subcounty" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={subCounties?.map((s) => ({
                        value: s?.subId,
                        label: s?.subCountyName,
                      }))}
                      onChange={handleWaterSubCountyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </>
              )}

              {waterSubCountyId && (
                <Form.Item
                  name="waterLocationId"
                  label="Location"
                  rules={[
                    { required: true, message: "Please select location" },
                  ]}
                >
                  <Select
                    allowClear
                    options={locations?.map((l) => ({
                      value: l?.locId,
                      label: l?.locLocation,
                    }))}
                    onChange={handleWaterLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {waterLocationId && (
                <Form.Item
                  name="waterSubLocationId"
                  label="Sublocation"
                  rules={[
                    { required: true, message: "Please select sublocation" },
                  ]}
                >
                  <Select
                    allowClear
                    options={subLocations?.map((sl) => ({
                      value: sl?.slocId,
                      label: sl?.slocLocation,
                    }))}
                    onChange={handleWaterSubLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {waterConstId && (
                <Form.Item
                  name="waterWardId"
                  label="Ward"
                  rules={[{ required: true, message: "Please select ward" }]}
                >
                  <Select
                    allowClear
                    options={dataWard?.map((w) => ({
                      value: w?.wdId,
                      label: w?.wdWard,
                    }))}
                    onChange={handleWaterWardChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              <Form.Item
                name="waterLat"
                label={"GPS Latitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add latitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="waterLong"
                label={"GPS Longitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add longitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="waterWaterSourceId"
                className=""
                label={"Water Source"}
                rules={[
                  {
                    required: true,
                    message: "Please select water source",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataWtrSrc?.length > 0 &&
                    dataWtrSrc?.map((item) => ({
                      value: item?.wtrId,
                      label: item?.wtrSource,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="waterTreatmentTypeId"
                className=""
                label={"Treatment Type"}
                rules={[
                  {
                    required: true,
                    message: "Please select treatment type",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataTreatType?.length > 0 &&
                    dataTreatType?.map((item) => ({
                      value: item?.ttId,
                      label: item?.ttType,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                label={"Major Crops"}
                name="waterCropsId"
                rules={[
                  {
                    required: true,
                    message: "Required field",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following crops
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataCrop?.length > 0 &&
                    dataCrop?.map((item) => (
                      <Select.Option key={item?.cropId} value={item?.cropId}>
                        {item?.cropName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="waterCapacity"
                label={"Plant Capacity"}
                rules={[
                  {
                    required: true,
                    message: "Please add plant capacity",
                  },
                ]}
              >
                <InputNumber
                  suffix={
                    <span>
                      M<sup>3</sup>
                    </span>
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="waterPipelineTypeId"
                className=""
                label={"Pipeline Type"}
                rules={[
                  {
                    required: true,
                    message: "Please select pipeline type",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataPipelineType?.length > 0 &&
                    dataPipelineType?.map((item) => ({
                      value: item?.ptId,
                      label: item?.ptPipeline,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="waterPipelineDistance"
                label={"Pipeline distance"}
                rules={[
                  {
                    required: true,
                    message: "Please add pipeline distance",
                  },
                ]}
              >
                <InputNumber
                  suffix={<span>M</span>}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="waterEstBeneficiaries"
                label={"Estimated Benaficiaries"}
                rules={[
                  {
                    required: true,
                    message: "Please add benaficiaries",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="waterAcreage"
                label={"Acreage"}
                rules={[
                  {
                    required: true,
                    message: "Please add Acreage",
                  },
                ]}
              >
                <InputNumber
                  suffix="Acres"
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                label={"Pressure System"}
                name="waterPressureSystemId"
                rules={[
                  {
                    required: true,
                    message: "Please add Pressure System",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following pressure types
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataPresSys?.length > 0 &&
                    dataPresSys?.map((item) => (
                      <Select.Option key={item?.wtrId} value={item?.wtrId}>
                        {item?.wtrPressure}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="waterCost"
                label={"Cost"}
                rules={[
                  {
                    required: true,
                    message: "Please add cost",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="waterStatus"
                className=""
                label={"Water Supply Status"}
                rules={[
                  {
                    required: true,
                    message: "Please select water supply status",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    wtrSupplyStatusData?.length > 0 &&
                    wtrSupplyStatusData?.map((item) => ({
                      value: item?.value,
                      label: item?.label,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                label="Start Date"
                name="waterStartDate"
                rules={[
                  {
                    required: true,
                    message: "Start date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="End Date"
                name="waterEndDate"
                rules={[
                  {
                    required: true,
                    message: "End date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="Time Frame"
                name="waterTimeFrame"
                rules={[
                  {
                    required: true,
                    message: "Time Frame date is required",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
              <Form.Item
                label="Source of Funding"
                name="waterSourceFundsId"
                rules={[
                  { required: true, message: "Source of Funding is required" },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following sources
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  tagRender={(props) => {
                    const { label, closable, onClose } = props;
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 8px",
                          margin: "2px",
                          borderRadius: "4px",
                          background: "#f5f5f5",
                        }}
                      >
                        {label}
                        {closable && (
                          <span
                            onClick={onClose}
                            style={{
                              marginLeft: "8px",
                              cursor: "pointer",
                              color: "red",
                            }}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    );
                  }}
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {subsidySources?.map((item) => (
                    <Select.Option
                      key={item?.subsidySourceId}
                      value={item?.subsidySourceId}
                    >
                      {item?.subsidySourceName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label={"Pathways"}
                name="waterPathwayId"
                rules={[
                  {
                    required: true,
                    message: "Please add pathways",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following pathways
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataPathway?.length > 0 &&
                    dataPathway?.map((item) => (
                      <Select.Option
                        key={item?.irrigationPathwayId}
                        value={item?.irrigationPathwayId}
                      >
                        {item?.irrigationPathwayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="waterMetered"
                label="Metered"
                rules={[
                  {
                    required: false,
                    message: "Metered field is required",
                  },
                ]}
              >
                <Radio.Group options={plainOptions} />
              </Form.Item>

              <Form.Item
                name="waterLoss"
                label={"Water Loss (M³)"}
                rules={[
                  {
                    required: true,
                    message: "Please add Water Loss (M³)",
                  },
                ]}
              >
                <InputNumber
                  suffix={
                    <span>
                      M<sup>3</sup>
                    </span>
                  }
                  min={0}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="waterAgencyId"
                className=""
                label={"Implementing Entity/Department"}
                rules={[
                  {
                    required: true,
                    message: "Please select Implementing Entity/Department",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataAgency?.length > 0 &&
                    dataAgency?.map((item) => ({
                      value: item?.agencyId,
                      label: item?.agencyName,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                name="waterManagingAgency"
                label={"Managing Entity"}
                rules={[
                  {
                    required: true,
                    message: "Please add Managing Entity",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
            </div>
            <Form.Item
              name="waterRemarks"
              label={"Remarks"}
              rules={[
                {
                  required: true,
                  message: "Please add remarks",
                },
              ]}
            >
              <TextArea
                placeholder="Description..."
                rows={5}
                className="text_area"
              />
            </Form.Item>
            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>

                <div className="w-[150px]">
                  <button key="submit" type="submit" className="cstm-btn-2">
                    {saving ? (
                      <Spin />
                    ) : activeObjWtrSupply?.waterId ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default WaterSupplyAdd;
